import { useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Icon, Input } from '@sendible/design-system/src';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { useReportInitialisation, useMenuInteractions, useReportActions } from './hooks';
import { HeaderActions, LoadingSkeleton, ErrorState, ModuleGrid } from './components';
import {
  Container,
  HeaderBar,
  HeaderLeft,
  BackButton,
  Toolbar,
  ToolbarLeft,
  ToolbarRight,
  ContentArea,
  SidebarWrapper,
  TitleContainer,
} from './index.styles';
import { useReportingContext } from '../../context';
import { resolveModuleType } from '../../../../models/reporting/transforms';
import { AccountSelector, DateRangePicker, EditorSidebar, DiscardChangesModal, DeleteReportModal } from '..';

export const ReportDetail = () => {
  const { t } = useTranslation('reports');
  const { modules, addModule, removeModule, reportName, setReportName } = useReportingContext();

  const {
    isNewReport,
    isLoadingReport,
    isReportError,
    report,
    allProfiles,
    moduleDefinitions,
    hasSnapshotted,
    latestSnapshotState,
    isOpenedFromTemplate,
  } = useReportInitialisation();

  const { isMenuOpen, menuRef, toggleMenu, closeMenu } = useMenuInteractions();

  const [triggerOpenAddPanel, setTriggerOpenAddPanel] = useState(0);

  const [isEditing, setIsEditing] = useState(() => isNewReport);

  const {
    isSaving,
    saveSuccess,
    isDirty,
    showDiscardModal,
    showDeleteModal,
    setShowDiscardModal,
    setShowDeleteModal,
    handleBack,
    handleCancelEdit,
    handleConfirmDiscard,
    handleDelete,
    handleDuplicate,
    handleSave,
  } = useReportActions({
    isNewReport,
    report,
    allProfiles,
    moduleDefinitions,
    reportName,
    setReportName,
    isEditing,
    setIsEditing,
    hasSnapshotted,
    latestSnapshotState,
    isOpenedFromTemplate,
  });

  const handleAddModule = useCallback(
    (definitionId: number) => {
      const definition = moduleDefinitions?.find((d) => d.id === definitionId);

      addModule(resolveModuleType(definitionId, moduleDefinitions), definitionId, definition);
    },
    [addModule, moduleDefinitions]
  );

  const handleScrollToModule = useCallback((reportModuleId: string) => {
    const el = document.querySelector(`[data-module-id="${reportModuleId}"]`);

    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, []);

  const portalTarget = document.getElementById('layer-root');

  if (!portalTarget) return null;

  // Loading state
  if (!isNewReport && isLoadingReport) {
    return createPortal(<LoadingSkeleton onBack={handleBack} />, portalTarget);
  }

  // Error / not found state
  if (!isNewReport && (!report || isReportError)) {
    return createPortal(
      <ErrorState
        isError={!!isReportError}
        onBack={handleBack}
      />,
      portalTarget
    );
  }

  let saveLabel = t('new_reports_save');

  if (isSaving) saveLabel = t('new_reports_saving');
  else if (saveSuccess) saveLabel = t('new_reports_saved');

  return createPortal(
    <Container>
      {/* ── Header bar: back, title, actions ── */}
      <HeaderBar $isEditing={isEditing}>
        <HeaderLeft>
          {!isEditing && (
            <BackButton
              type="button"
              onClick={handleBack}
              aria-label={t('new_reports_close_report') as string}
            >
              <Icon
                color="brandDark"
                name="close"
              />
            </BackButton>
          )}
          <TitleContainer>
            <Input
              adaptWidthToText
              change={setReportName}
              value={reportName}
              placeholder={t('new_reports_untitled_report') as string}
              size="md"
              label={t('new_reports_report_label') as string}
              button={isEditing ? { icon: 'edit', disabled: !isEditing } : undefined}
              maxLength={100}
              isDisabled={!isEditing}
            />
          </TitleContainer>
        </HeaderLeft>

        <HeaderActions
          isEditing={isEditing}
          isSaving={isSaving}
          isDirty={isDirty}
          saveLabel={saveLabel}
          menuRef={menuRef}
          isMenuOpen={isMenuOpen}
          onEdit={() => setIsEditing(true)}
          onCancelEdit={handleCancelEdit}
          onSave={handleSave}
          onToggleMenu={toggleMenu}
          onCloseMenu={closeMenu}
          onDuplicate={handleDuplicate}
          onClose={handleBack}
          onDelete={() => setShowDeleteModal(true)}
        />
      </HeaderBar>

      {/* ── Toolbar: accounts + date range ── */}
      <Toolbar>
        <ToolbarLeft>
          <AccountSelector />
        </ToolbarLeft>
        <ToolbarRight>
          <DateRangePicker disabled={false} />
        </ToolbarRight>
      </Toolbar>

      {/* ── Content area: sidebar + module grid ── */}
      <ContentArea>
        <SidebarWrapper $isVisible={isEditing}>
          <EditorSidebar
            triggerOpenAddPanel={triggerOpenAddPanel}
            currentModules={modules.map((m) => ({
              reportModuleId: m.reportModuleId,
              moduleType: m.moduleType,
              customName: m.customName,
            }))}
            onAddModule={handleAddModule}
            onScrollToModule={handleScrollToModule}
            disabled={!isEditing}
          />
        </SidebarWrapper>

        <ModuleGrid
          isEditing={isEditing}
          moduleDefinitions={moduleDefinitions}
          onRemoveModule={removeModule}
          onOpenAddPanel={isEditing ? () => setTriggerOpenAddPanel((n) => n + 1) : undefined}
        />
      </ContentArea>

      {showDiscardModal && (
        <DiscardChangesModal
          onDiscard={handleConfirmDiscard}
          onCancel={() => setShowDiscardModal(false)}
        />
      )}

      {showDeleteModal && (
        <DeleteReportModal
          reportName={reportName}
          onDelete={handleDelete}
          onCancel={() => setShowDeleteModal(false)}
        />
      )}
    </Container>,
    portalTarget
  );
};
